import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPaymentStatus } from '../services/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [isAuthenticated, navigate, searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const data = await getPaymentStatus(sessionId);
      
      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('error');
      } else if (attempts < maxAttempts) {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      if (attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[#EBE8DF] p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D97757] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
              Processing Payment
            </h1>
            <p className="text-[#596652]">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-[#4CAF50] mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
              Payment Successful!
            </h1>
            <p className="text-[#596652] mb-6">
              Your rental has been confirmed. You can now coordinate with the owner to pick up the item.
            </p>
            <div className="flex gap-3">
              <Link to="/dashboard" className="flex-1" data-testid="go-to-dashboard">
                <Button className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547]">
                  Go to Dashboard
                </Button>
              </Link>
              <Link to="/messages" className="flex-1" data-testid="go-to-messages">
                <Button variant="outline" className="w-full border-[#EBE8DF] rounded-full">
                  Messages
                </Button>
              </Link>
            </div>
          </>
        )}

        {(status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 text-[#E53935] mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-semibold text-[#2C3329] mb-2">
              {status === 'timeout' ? 'Payment Verification Timeout' : 'Payment Failed'}
            </h1>
            <p className="text-[#596652] mb-6">
              {status === 'timeout' 
                ? 'We couldn\'t verify your payment status. Please check your dashboard or contact support.'
                : 'Something went wrong with your payment. Please try again.'}
            </p>
            <Link to="/dashboard" data-testid="return-to-dashboard">
              <Button className="w-full bg-[#D97757] text-white rounded-full hover:bg-[#C56547]">
                Return to Dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
